<script setup>
import api from '@/services/api'
import { ref, watch } from 'vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  itemDetalhes: { type: Object, required: true },
  listas: { type: Object, required: true },
  formatarValor: { type: Function, default: (v) => v },
  getTextoLista: { type: Function, default: (l, id) => id || '-' },
  formatarMoedaComSigla: { type: Function, default: (v, m) => `${v} ${m}` }
})

// Busca os dados da PO separadamente (o DTO do workflow é agnóstico)
const poDetalhes = ref(null)
const loadingPO = ref(false)

watch(() => props.itemDetalhes?.idPurchaseOrder, async (id) => {
  if (id) {
    loadingPO.value = true
    try {
      const res = await api.get(`/PurchaseOrders/${id}`, { _suppressLoading: true })
      poDetalhes.value = res.data
    } catch (e) {
      poDetalhes.value = null
    } finally {
      loadingPO.value = false
    }
  } else {
    poDetalhes.value = null
  }
}, { immediate: true })
</script>

<template>
  <!-- Resumo em destaque -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">
      <i class="bi bi-chat-quote me-1 text-info"></i> Resumo
    </small>
    <p class="mb-0 text-body-secondary small">{{ itemDetalhes.resumoSolicitacao || 'Nenhum resumo registrado.' }}</p>
    <small v-if="itemDetalhes.nomeUsuarioCriacao" class="text-body-secondary d-block mt-1 small">
      <i class="bi bi-person-fill me-1 text-info"></i> Criado por: <span class="fw-medium">{{ itemDetalhes.nomeUsuarioCriacao }}</span>
    </small>
  </div>

  <!-- Loading -->
  <div v-if="loadingPO" class="text-center py-3">
    <div class="spinner-border spinner-border-sm text-info me-2"></div>
    <span class="text-body-secondary small">Carregando dados da Purchase Order...</span>
  </div>

  <!-- Grid completo de informações (dados da PO) -->
  <template v-else-if="poDetalhes">
    <div class="row g-2 mb-3">
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">PO #</small>
        <span class="fw-medium small">{{ poDetalhes.numeroPurchaseOrder || '#' + poDetalhes.idPurchaseOrder }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Fornecedor</small>
        <span class="fw-medium small">{{ poDetalhes.nomeFornecedor || '-' }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
        <span class="badge badge-ghost-info">{{ getTextoLista(listas.LstStatusPurchaseOrder, poDetalhes.lstStatusPurchaseOrder) }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Data Emissão</small>
        <span class="small">{{ formatarDataBR(poDetalhes.dataEmissao) }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Previsão Produção</small>
        <span class="small">{{ poDetalhes.previsaoProducao ? formatarDataBR(poDetalhes.previsaoProducao) : '-' }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Previsão ETD</small>
        <span class="small">{{ poDetalhes.previsaoEmbarqueETD ? formatarDataBR(poDetalhes.previsaoEmbarqueETD) : '-' }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Previsão ETA</small>
        <span class="small">{{ poDetalhes.previsaoEmbarqueETA ? formatarDataBR(poDetalhes.previsaoEmbarqueETA) : '-' }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Incoterm</small>
        <span class="small">{{ getTextoLista(listas.LstIncoterm, poDetalhes.lstIncoterm) }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Forma Pagamento</small>
        <span class="small">{{ getTextoLista(listas.LstFormaPagamento, poDetalhes.lstFormaPagamento) }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Moeda</small>
        <span class="small">{{ getTextoLista(listas.LstMoeda, poDetalhes.lstMoeda) }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Contrato Fornecedor</small>
        <span class="small">{{ poDetalhes.numeroContratoFornecedor || '-' }}</span>
      </div>
      <div class="col-md-3">
        <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
        <span class="small">{{ itemDetalhes.nomeGrupoAprovador || '-' }}</span>
      </div>
    </div>

    <!-- Breakdown de valores -->
    <div class="bg-body-tertiary border rounded p-2 mb-3">
      <small class="fw-bold text-body-emphasis d-block mb-1">
        <i class="bi bi-calculator me-1 text-info"></i> Breakdown de Valores
      </small>
      <div class="row g-1 small">
        <div class="col-6 col-md-3">
          <span class="text-body-secondary">Original:</span>
          <span class="fw-medium ms-1">{{ formatarValor(poDetalhes.valorTotalOriginal) }}</span>
        </div>
        <div class="col-6 col-md-3">
          <span class="text-body-secondary">Desconto:</span>
          <span class="fw-medium ms-1">{{ formatarValor(poDetalhes.valorTotalDesconto) }}</span>
        </div>
        <div class="col-6 col-md-3">
          <span class="text-body-secondary">Frete:</span>
          <span class="fw-medium ms-1">{{ formatarValor(poDetalhes.valorTotalFrete) }}</span>
        </div>
        <div class="col-6 col-md-3">
          <span class="text-body-secondary fw-bold">Negociado:</span>
          <span class="fw-bold ms-1">{{ formatarValor(poDetalhes.valorTotalNegociado) }}</span>
        </div>
      </div>
    </div>

    <!-- Condições de Pagamento -->
    <div v-if="poDetalhes.condicoesPagamento && poDetalhes.condicoesPagamento.length > 0" class="mb-2">
      <small class="fw-bold text-body-emphasis d-block mb-1">
        <i class="bi bi-credit-card me-1 text-info"></i> Condições de Pagamento ({{ poDetalhes.condicoesPagamento.length }})
      </small>
      <div class="table-responsive itens-scroll-wrapper">
        <table class="table table-sm table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th style="font-size: 0.75rem;">Descrição</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(cond, idx) in poDetalhes.condicoesPagamento" :key="idx">
              <td class="small">{{ cond.descricao || `${cond.percentualDoTotal || 0}% do total` }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else class="text-center text-body-secondary small py-2 mb-2">
      <i class="bi bi-info-circle me-1"></i> Nenhuma condição de pagamento registrada.
    </div>

    <!-- Tabela completa de produtos -->
    <div v-if="poDetalhes.produtosSolicitados && poDetalhes.produtosSolicitados.length > 0" class="mb-2">
      <small class="fw-bold text-body-emphasis d-block mb-1">
        <i class="bi bi-box-seam me-1 text-info"></i> Produtos ({{ poDetalhes.produtosSolicitados.length }})
      </small>
      <div class="table-responsive itens-scroll-wrapper">
        <table class="table table-sm table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th style="font-size: 0.75rem;">Produto</th>
              <th class="text-end" style="font-size: 0.75rem;">Qtd Forn</th>
              <th class="text-center" style="font-size: 0.75rem;">Un</th>
              <th class="text-end" style="font-size: 0.75rem;">Vlr Unit</th>
              <th class="text-end" style="font-size: 0.75rem;">Vlr Total</th>
              <th style="font-size: 0.75rem;">Observação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(prod, idx) in poDetalhes.produtosSolicitados" :key="idx">
              <td>
                <span class="fw-medium text-body-emphasis small">{{ prod.nomeProduto || prod.sku || 'Produto #' + prod.idProduto }}</span>
                <small v-if="prod.sku && prod.nomeProduto" class="d-block text-body-secondary" style="font-size: 0.65rem;">SKU: {{ prod.sku }}</small>
                <small v-else-if="!prod.sku && !prod.nomeProduto && prod.idProduto" class="d-block text-body-secondary" style="font-size: 0.65rem;">ID: {{ prod.idProduto }}</small>
              </td>
              <td class="text-end fw-medium small">{{ formatarValor(prod.quantidadeUnFornecedorNegociada) }}</td>
              <td class="text-center small">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, prod.lstUnidadeMedidaFornecedor) }}</td>
              <td class="text-end small">{{ formatarValor(prod.valorUnitarioOriginal) }}</td>
              <td class="text-end fw-medium small">{{ formatarValor(prod.valorTotalNegociado) }}</td>
              <td class="small text-body-secondary">{{ prod.observacao || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else class="text-center text-body-secondary small py-2">
      Nenhum produto registrado.
    </div>
  </template>

  <!-- Fallback sem dados da PO -->
  <div v-else class="text-center text-body-secondary small py-3">
    <i class="bi bi-info-circle me-1"></i> Purchase Order não encontrada ou sem dados complementares.
  </div>
</template>

<style scoped>
.itens-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
}

.itens-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}

.itens-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}

.item-row:last-child {
  border-bottom: none !important;
}
</style>
